import React, { useState } from 'react'
import { PinBoardWidget, WidgetFormData } from '../../types'
import { Button } from '../ui'
import { pinBoardApi } from '../../services/api'

interface WidgetsSectionProps {
  widgets: PinBoardWidget[]
  onWidgetsChange: () => void
}

export const WidgetsSection: React.FC<WidgetsSectionProps> = ({
  widgets,
  onWidgetsChange
}) => {
  const [showAddForm, setShowAddForm] = useState(false)
  const [formData, setFormData] = useState<WidgetFormData>({
    title: '',
    type: 'iframe',
    content: '',
    size: 'medium'
  })
  const [saving, setSaving] = useState(false)

  const widgetTypes = [
    { value: 'iframe', label: 'Website/Embed (iframe)' },
    { value: 'image', label: 'Image' },
    { value: 'video', label: 'Video' },
    { value: 'custom', label: 'Custom HTML' }
  ]

  const sizeOptions = [
    { value: 'small', label: 'Small', gridClass: 'col-span-1' },
    { value: 'medium', label: 'Medium', gridClass: 'col-span-2' },
    { value: 'large', label: 'Large', gridClass: 'col-span-3' }
  ]

  const resetForm = () => {
    setFormData({
      title: '',
      type: 'iframe',
      content: '',
      size: 'medium'
    })
    setShowAddForm(false)
  }

  const handleSave = async () => {
    if (!formData.title.trim() || !formData.content.trim()) {
      alert('Please fill in both title and content')
      return
    }

    try {
      setSaving(true)
      
      const payload = {
        title: formData.title.trim(),
        type: formData.type,
        content: formData.content.trim(),
        size: formData.size,
        grid_row: 0,
        grid_col: 0
      }

      await pinBoardApi.createWidget(payload)
      resetForm()
      onWidgetsChange()
    } catch (error) {
      console.error('Failed to save widget:', error)
      alert('Failed to save widget. Please try again.')
    } finally {
      setSaving(false)
    }
  }

  const handleDelete = async (widgetId: number) => {
    if (!window.confirm('Are you sure you want to delete this widget?')) {
      return
    }

    try {
      await pinBoardApi.deleteWidget(widgetId)
      onWidgetsChange()
    } catch (error) {
      console.error('Failed to delete widget:', error)
      alert('Failed to delete widget. Please try again.')
    }
  }

  const getGridClass = (size: string) => {
    const sizeOption = sizeOptions.find(opt => opt.value === size)
    return sizeOption?.gridClass || 'col-span-2'
  }

  const renderWidget = (widget: PinBoardWidget) => {
    switch (widget.type) {
      case 'iframe':
        return (
          <iframe
            src={widget.content}
            className="w-full h-64 rounded border-0"
            title={widget.title}
            sandbox="allow-scripts allow-same-origin"
          />
        )
      case 'image':
        return (
          <img
            src={widget.content}
            alt={widget.title}
            className="w-full h-64 object-cover rounded"
          />
        )
      case 'video':
        return (
          <video
            src={widget.content}
            className="w-full h-64 rounded"
            controls
          />
        )
      case 'custom':
        return (
          <div
            className="w-full h-64 overflow-auto p-4 bg-gray-50 rounded"
            dangerouslySetInnerHTML={{ __html: widget.content }}
          />
        )
      default:
        return (
          <div className="w-full h-64 bg-gray-100 rounded flex items-center justify-center text-gray-500">
            Unsupported widget type
          </div>
        )
    }
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold text-gray-900">Embedded Content</h3>
        <Button
          onClick={() => setShowAddForm(true)}
          size="sm"
          variant="primary"
          disabled={showAddForm}
        >
          + Add Widget
        </Button>
      </div>

      {/* Widgets Grid */}
      <div className="mb-4">
        {widgets.length === 0 ? (
          <p className="text-gray-500 text-sm py-8 text-center border-2 border-dashed border-gray-200 rounded-lg">
            No widgets yet. Add some embedded content like dashboards, images, or videos!
          </p>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {widgets.map((widget) => (
              <div key={widget.id} className={`group relative ${getGridClass(widget.size)}`}>
                <div className="bg-white border border-gray-200 rounded-lg p-4 shadow-sm">
                  <div className="flex items-center justify-between mb-3">
                    <h4 className="font-medium text-gray-900 truncate">{widget.title}</h4>
                    <button
                      onClick={() => handleDelete(widget.id)}
                      className="opacity-0 group-hover:opacity-100 transition-opacity duration-200 text-red-600 hover:text-red-700"
                      title="Delete widget"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                      </svg>
                    </button>
                  </div>
                  {renderWidget(widget)}
                  <div className="mt-2 text-xs text-gray-500">
                    {widget.type} • {widget.size}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Add Widget Form */}
      {showAddForm && (
        <div className="border border-gray-200 rounded-lg p-4 bg-gray-50">
          <h4 className="font-medium text-gray-900 mb-3">Add Widget</h4>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Title *
              </label>
              <input
                type="text"
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                placeholder="e.g., Dashboard, Chart, Video"
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-sky-500 focus:border-transparent"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Type
              </label>
              <select
                value={formData.type}
                onChange={(e) => setFormData({ ...formData, type: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-sky-500 focus:border-transparent"
              >
                {widgetTypes.map((type) => (
                  <option key={type.value} value={type.value}>
                    {type.label}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Size
              </label>
              <select
                value={formData.size}
                onChange={(e) => setFormData({ ...formData, size: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-sky-500 focus:border-transparent"
              >
                {sizeOptions.map((size) => (
                  <option key={size.value} value={size.value}>
                    {size.label}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div className="mt-4">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Content *
            </label>
            <textarea
              value={formData.content}
              onChange={(e) => setFormData({ ...formData, content: e.target.value })}
              placeholder={
                formData.type === 'iframe' ? 'https://example.com/dashboard' :
                formData.type === 'image' ? 'https://example.com/image.jpg' :
                formData.type === 'video' ? 'https://example.com/video.mp4' :
                '<div>Custom HTML content</div>'
              }
              rows={3}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-sky-500 focus:border-transparent"
            />
            <p className="text-xs text-gray-500 mt-1">
              {formData.type === 'iframe' && 'Enter the URL to embed (must support iframe embedding)'}
              {formData.type === 'image' && 'Enter the image URL'}
              {formData.type === 'video' && 'Enter the video URL'}
              {formData.type === 'custom' && 'Enter custom HTML content (use with caution)'}
            </p>
          </div>

          <div className="flex space-x-3 mt-4">
            <Button
              onClick={handleSave}
              disabled={saving}
              loading={saving}
              variant="primary"
              size="sm"
            >
              Add Widget
            </Button>
            <Button
              onClick={resetForm}
              disabled={saving}
              variant="secondary"
              size="sm"
            >
              Cancel
            </Button>
          </div>
        </div>
      )}
    </div>
  )
}
